/**
 * Returns the native language name for a given language code.
 * Always displays the language name in its own native form, regardless of the current UI locale.
 *
 * @param languageCode - The language code (e.g., 'en', 'de', 'el', 'ja-JP')
 * @returns The native language name (e.g., 'English', 'Deutsch', 'Ελληνικά', '日本語')
 */
export function getNativeLanguageLabel(languageCode: string): string {
  if (!languageCode) {
    return languageCode;
  }

  try {
    // Normalize the language code: trim whitespace and convert underscores to hyphens
    const normalized = languageCode.trim().replace(/_/g, '-');

    // Use Intl.DisplayNames with the language code itself as the locale
    // This ensures we get the native name of the language
    const displayNames = new Intl.DisplayNames([normalized], {
      type: 'language',
    });

    const label = displayNames.of(normalized) || languageCode;

    // Capitalize the first letter for UI consistency across languages
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    // Fallback to the original code if Intl.DisplayNames fails
    return languageCode;
  }
}

/**
 * Returns the localized language name for a given language code.
 * Uses the current locale to get the localized language name.
 *
 * @param languageCode - The language code (e.g., 'en', 'de', 'el', 'ja-JP')
 * @param currentLocale - The current locale (e.g., 'en-US', 'de-DE', 'el-GR', 'ja-JP')
 * @returns The localized language name (e.g., 'English', 'Deutsch', 'Ελληνικά', '日本語')
 */
export function getLocalizedLanguageLabel(
  languageCode: string,
  currentLocale: string
): string {
  if (!languageCode) {
    return languageCode;
  }

  try {
    // Normalize the language code: trim whitespace and convert underscores to hyphens
    const normalized = languageCode.trim().replace(/_/g, '-');

    const displayNames = new Intl.DisplayNames([currentLocale], {
      type: 'language',
    });
    return displayNames.of(normalized) || languageCode;
  } catch {
    // Fallback to the original code if Intl.DisplayNames fails
    return languageCode;
  }
}
